import { Link } from 'react-router-dom';


function AffiliateTerms() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/" className="text-blue-600 hover:text-blue-700 font-semibold">
            ← Back to Home
          </Link>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white shadow-lg rounded-lg p-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            UPost Affiliate Program Agreement
          </h1>
          <p className="text-slate-600 mb-8">
            Last Updated: January 2026
          </p>

          <div className="prose prose-slate max-w-none">
            <h2>1. Agreement to Terms</h2>
            <p>
              By enrolling in the UPost Affiliate Program ("Program"), you ("Affiliate") agree to be bound by these terms and conditions ("Agreement"). This Agreement governs your participation in the Program and your right to earn commissions on qualifying referrals.
            </p>

            <h2>2. Account Activation & Approval</h2>
            <p>
              Upon successful application, your affiliate account will be immediately activated. You may begin promoting UPost and earning commissions right away. UPost reserves the right to approve or reject any affiliate application at its sole discretion.
            </p>

            <h2>3. Account Activity Requirements</h2>
            
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-4">
              <h3 className="text-lg font-bold text-yellow-900 mt-0">⚠️ Important: Active Account Policy</h3>
              
              <p className="mb-2"><strong>To maintain an active account, you must demonstrate activity within 30 days of signup.</strong></p>
              
              <p className="mb-2">Activity is defined as any ONE of the following:</p>
              <ul className="mb-2">
                <li>Logging into your affiliate dashboard</li>
                <li>Generating clicks on your referral link</li>
                <li>Referring a customer (lead or conversion)</li>
                <li>Communicating with the affiliate support team</li>
              </ul>
            </div>

            <h3>3.1 Inactive Account Suspension</h3>
            <p>
              <strong>Accounts that remain inactive for 30 consecutive days will be automatically suspended.</strong> Upon suspension:
            </p>
            <ul>
              <li>Your affiliate code and referral links will be deactivated</li>
              <li>You will no longer be eligible to earn commissions on new referrals</li>
              <li>Access to the affiliate dashboard will be restricted</li>
              <li>Any pending commissions under $100 may be forfeited</li>
              <li>Earned commissions over $100 will remain available for payout upon reactivation</li>
            </ul>

            <h3>3.2 Inactivity Warnings</h3>
            <p>
              UPost will send email notifications before suspension:
            </p>
            <ul>
              <li><strong>20 days inactive:</strong> First warning (10 days to reactivate)</li>
              <li><strong>25 days inactive:</strong> Second warning (5 days to reactivate)</li>
              <li><strong>28 days inactive:</strong> Final warning (2 days to reactivate)</li>
              <li><strong>30 days inactive:</strong> Account suspended</li>
            </ul>

            <h3>3.3 Reactivation Process</h3>
            <p>
              If your account is suspended due to inactivity, you must submit a reactivation request that includes:
            </p>
            <ol>
              <li>Explanation for the period of inactivity</li>
              <li>Promotional plan for the next 30 days</li>
              <li>Commitment to maintain minimum activity standards</li>
            </ol>
            <p>
              UPost will review your request within 3-5 business days. Approval is not guaranteed and is granted at our sole discretion.
            </p>

            <h2>4. Commission Structure</h2>
            
            <h3>4.1 Performance-Based Tiers</h3>
            <table className="min-w-full border-collapse border border-slate-300 my-4">
              <thead className="bg-slate-100">
                <tr>
                  <th className="border border-slate-300 px-4 py-2">Tier</th>
                  <th className="border border-slate-300 px-4 py-2">Referrals</th>
                  <th className="border border-slate-300 px-4 py-2">Commission Rate</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-slate-300 px-4 py-2">🥉 Bronze</td>
                  <td className="border border-slate-300 px-4 py-2">1-10</td>
                  <td className="border border-slate-300 px-4 py-2">10%</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 px-4 py-2">🥈 Silver</td>
                  <td className="border border-slate-300 px-4 py-2">11-25</td>
                  <td className="border border-slate-300 px-4 py-2">15%</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 px-4 py-2">🥇 Gold</td>
                  <td className="border border-slate-300 px-4 py-2">26-50</td>
                  <td className="border border-slate-300 px-4 py-2">20%</td>
                </tr>
                <tr>
                  <td className="border border-slate-300 px-4 py-2">💎 Platinum</td>
                  <td className="border border-slate-300 px-4 py-2">51+</td>
                  <td className="border border-slate-300 px-4 py-2">25%</td>
                </tr>
              </tbody>
            </table>

            <h3>4.2 Milestone Bonuses</h3>
            <ul>
              <li>5 referrals: $25 bonus</li>
              <li>10 referrals: $50 bonus</li>
              <li>25 referrals: $100 bonus</li>
              <li>50 referrals: $250 bonus</li>
              <li>100 referrals: $500 bonus</li>
            </ul>

            <h3>4.3 Commission Eligibility</h3>
            <p>
              <strong>Only active affiliates are eligible to earn commissions.</strong> Commissions are earned only when:
            </p>
            <ul>
              <li>Your account is in good standing (active, not suspended)</li>
              <li>The referred customer completes a qualified purchase</li>
              <li>Payment is received by UPost</li>
              <li>The sale is not refunded or charged back within 60 days</li>
            </ul>

            <h2>5. Payout Terms</h2>
            
            <h3>5.1 Minimum Payout</h3>
            <p>
              The minimum payout threshold is <strong>$100</strong>. Commissions will accumulate until this threshold is reached.
            </p>

            <h3>5.2 Payout Schedule</h3>
            <p>
              Payouts are processed monthly, within 15 business days after month-end, for affiliates who:
            </p>
            <ul>
              <li>Have an active account</li>
              <li>Have earnings exceeding $100</li>
              <li>Have submitted required tax documentation (W-9 form)</li>
              <li>Have verified their identity</li>
            </ul>

            <h3>5.3 Suspended Account Payouts</h3>
            <p>
              If your account is suspended:
            </p>
            <ul>
              <li>Pending commissions <strong>under $100 will be forfeited</strong></li>
              <li>Commissions <strong>over $100 will be held</strong> and paid upon successful reactivation</li>
              <li>Held commissions will be paid within 30 days of reactivation approval</li>
            </ul>

            <h2>6. Prohibited Activities</h2>
            <p>
              The following activities are strictly prohibited and will result in immediate account suspension or termination:
            </p>
            <ul>
              <li>False or misleading advertising</li>
              <li>Spam or unsolicited email marketing</li>
              <li>Self-referrals or fraudulent conversions</li>
              <li>Cookie stuffing or other deceptive tracking methods</li>
              <li>Trademark or copyright infringement</li>
              <li>Bidding on branded keywords in paid search</li>
              <li>Using automated bots or click farms</li>
            </ul>

            <h2>7. Termination</h2>
            
            <h3>7.1 By UPost</h3>
            <p>
              We reserve the right to terminate your affiliate account at any time for:
            </p>
            <ul>
              <li>Violation of this Agreement</li>
              <li>Fraudulent activity or suspicious behavior</li>
              <li>Extended inactivity (over 90 days after suspension)</li>
              <li>Damage to UPost brand or reputation</li>
            </ul>

            <h3>7.2 By Affiliate</h3>
            <p>
              You may terminate this Agreement at any time by contacting support@upost.com. Upon termination:
            </p>
            <ul>
              <li>Your referral links will be deactivated</li>
              <li>Earned commissions over $100 will be paid within 30 days</li>
              <li>Commissions under $100 may be forfeited</li>
            </ul>

            <h2>8. Tax Reporting</h2>
            <p>
              Affiliates earning $600 or more in a calendar year will receive IRS Form 1099-NEC for tax reporting purposes. You are responsible for all applicable taxes on your earnings.
            </p>

            <h2>9. Modifications to Agreement</h2>
            <p>
              UPost reserves the right to modify this Agreement at any time. Material changes will be communicated via email. Continued participation in the Program after modifications constitutes acceptance of the updated terms.
            </p>

            <h2>10. Limitation of Liability</h2>
            <p>
              UPost shall not be liable for any indirect, incidental, special, or consequential damages arising from your participation in the Program.
            </p>

            <h2>11. Contact Information</h2>
            <p>
              For questions about this Agreement or the Affiliate Program, contact:<br />
              <strong>Email:</strong> affiliates@upost.com<br />
              <strong>Phone:</strong> (555) 123-4567
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 my-6">
              <h3 className="text-lg font-bold text-blue-900 mt-0">📋 Acknowledgment</h3>
              <p className="mb-0">
                By signing up for the UPost Affiliate Program, you acknowledge that you have read, understood, and agree to be bound by these terms and conditions, including the <strong>30-day activity requirement</strong> and <strong>account suspension policy</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default AffiliateTerms;
